import { FC } from 'react';
import { StyleSheet, View } from '@react-pdf/renderer';
import Typography from '../../../Pdf/components/Typography';
import Row from '../../../Pdf/components/Row';
import theme from '../../../Pdf/components/theme';
import { PeriodEndReportFundOverviewDataPoint } from '../../types';

const pad = {
  firstTop: '14px',
  firstBottom: '10px',
  mid: '10px',
  lastTop: '10px',
  lastBottom: '14px',
  betweenRowAndSubRow: '8px',
  betweenSubRows: '4px',
  subRowLeftIndent: '8px',
  dividerInset: '14px',
} as const;

function mainRowPadding(options: {
  isFirstTableRow: boolean;
  isLastTableRow: boolean;
  hasSubRows: boolean;
}) {
  return {
    paddingTop: options.isFirstTableRow ? pad.firstTop : pad.lastTop,
    paddingBottom: options.hasSubRows
      ? pad.betweenRowAndSubRow
      : options.isLastTableRow
      ? pad.lastBottom
      : pad.firstBottom,
  };
}

function subRowPadding(options: {
  isFirstSubRow: boolean;
  isLastTableRow: boolean;
  isLastSubRow: boolean;
}) {
  return {
    paddingTop: options.isFirstSubRow ? '0px' : pad.betweenSubRows,
    paddingBottom: options.isLastSubRow
      ? options.isLastTableRow
        ? pad.lastBottom
        : pad.firstBottom
      : '0px',
  };
}

const styles = StyleSheet.create({
  dataPointBlock: {
    borderBottomWidth: 0.75,
    borderBottomColor: theme.palette.grey[300],
    borderBottomStyle: 'solid',
  },
  dataPointBlockLast: {
    borderBottomWidth: 0,
  },
  stretchRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'stretch',
    width: '100%',
  },
  leftHalf: {
    width: '50%',
    backgroundColor: theme.palette.grey[50],
    borderRightWidth: 0.75,
    borderRightColor: theme.palette.grey[300],
    borderRightStyle: 'solid',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  rightHalf: {
    width: '50%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  subLeftHalf: {
    width: '50%',
    backgroundColor: theme.palette.grey[50],
    borderRightWidth: 0.75,
    borderRightColor: theme.palette.grey[300],
    borderRightStyle: 'solid',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  subRightHalf: {
    width: '50%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  valueRow: {
    gap: '2px',
  },
});

type Props = {
  dataPoint: PeriodEndReportFundOverviewDataPoint;
  index: number;
  totalCount: number;
};

const FundOverviewDataPoint: FC<Props> = ({ dataPoint, index, totalCount }) => {
  const isLastBlock = index === totalCount - 1;
  const hasSubRows = Boolean(dataPoint.subRows?.length);
  const subRows = dataPoint.subRows ?? [];

  const mainIsFirstTableRow = index === 0;
  const mainIsLastTableRow = isLastBlock && !hasSubRows;
  const mainCellPadding = mainRowPadding({
    isFirstTableRow: mainIsFirstTableRow,
    isLastTableRow: mainIsLastTableRow,
    hasSubRows,
  });

  const leftMainPadding = {
    ...mainCellPadding,
    paddingRight: pad.dividerInset,
  };

  const rightMainPadding = {
    ...mainCellPadding,
    paddingLeft: pad.dividerInset,
  };

  return (
    <View
      style={[
        styles.dataPointBlock,
        ...(isLastBlock ? [styles.dataPointBlockLast] : []),
      ]}
    >
      <View style={styles.stretchRow}>
        <View style={[styles.leftHalf, leftMainPadding]}>
          <Typography variant="body2" color={theme.palette.grey[900]} bold>
            {dataPoint.label}
          </Typography>
        </View>
        <View style={[styles.rightHalf, rightMainPadding]}>
          <Row centered style={styles.valueRow}>
            <Typography variant="body2" color={theme.palette.grey[900]} bold>
              {dataPoint.value}
            </Typography>
            {dataPoint.detail ? (
              <Typography variant="body2" color={theme.palette.grey[600]}>
                {dataPoint.detail}
              </Typography>
            ) : null}
          </Row>
        </View>
      </View>
      {subRows.map((subRow, subIndex) => {
        const isFirstSubRow = subIndex === 0;
        const isLastSubRow = subIndex === subRows.length - 1;
        const subIsLastTableRow = isLastBlock && isLastSubRow;
        const subCellVerticalPadding = subRowPadding({
          isFirstSubRow,
          isLastSubRow,
          isLastTableRow: subIsLastTableRow,
        });
        const subLeftPadding = {
          ...subCellVerticalPadding,
          paddingRight: pad.dividerInset,
        };

        return (
          <View key={subRow.id} style={styles.stretchRow}>
            <View style={[styles.subLeftHalf, subLeftPadding]}>
              <Typography
                variant="body2"
                color={theme.palette.grey[600]}
                style={{
                  paddingLeft: pad.subRowLeftIndent,
                }}
              >
                {subRow.label}
              </Typography>
            </View>
            <View style={[styles.subRightHalf, subCellVerticalPadding]}>
              <Typography
                variant="body2"
                color={theme.palette.grey[600]}
                style={{
                  paddingLeft: pad.dividerInset,
                }}
              >
                {subRow.value}
              </Typography>
            </View>
          </View>
        );
      })}
    </View>
  );
};

export default FundOverviewDataPoint;
